import { useEffect, useState } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { api, ApiError } from '@/auth/api';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';

/**
 * Pre-auth "Having trouble signing in?" (Support v1) — the sign-in screen's quiet
 * door. Same low-threshold form MINUS the screenshot, PLUS a reply email
 * prefilled from whatever the person typed into the sign-in box. Text only;
 * the server brakes it per IP.
 */
export function PreAuthFeedback({ prefillEmail }: { prefillEmail: string }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sendLock = useActionLock();

  // Follow the sign-in box until the person edits the reply field themselves.
  const [emailTouched, setEmailTouched] = useState(false);
  useEffect(() => {
    if (!emailTouched) setEmail(prefillEmail);
  }, [prefillEmail, emailTouched]);

  async function send() {
    setError(null);
    try {
      await api('/v1/support-reports/pre-auth', {
        method: 'POST',
        unauthenticated: true,
        body: {
          message: message.trim(),
          reporterEmail: email.trim(),
          app: 'dispatcher',
          appVersion: __APP_VERSION__,
          build: __BUILD_TIME__,
          screen: '/login',
          deviceOs: navigator.userAgent.slice(0, 80),
        },
      });
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many reports from this connection — give it an hour.'
          : "Couldn't send it. Check the email looks right and try again.",
      );
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="w-full text-center text-xs text-breaker-mute hover:text-breaker-ink transition-colors"
        onClick={() => setOpen(true)}
      >
        Having trouble signing in?
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-breaker-line bg-white p-4 space-y-3 text-left">
      {sent ? (
        <>
          <p className="text-sm text-breaker-ink">Sent — Breaker support will reply by email.</p>
          <Button size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-breaker-ink">Contact support</p>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Tell us what's happening — nothing's too small to mention."
            className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm text-breaker-ink placeholder:text-breaker-mute focus:outline-none focus:ring-2 focus:ring-breaker-line-2"
          />
          <Field label="Email address for our reply" htmlFor="preauth-email">
            <Input
              id="preauth-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmailTouched(true);
                setEmail(e.target.value);
              }}
              placeholder="you@fleet.example"
            />
          </Field>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="text-xs text-breaker-mute hover:text-breaker-ink"
              onClick={() => setOpen(false)}
            >
              Never mind
            </button>
            <Button
              size="sm"
              disabled={message.trim().length === 0 || !/.+@.+\..+/.test(email.trim())}
              loading={sendLock.locked}
              onClick={() => sendLock.run(send)}
            >
              Send
            </Button>
          </div>
          {error ? <p className="text-sm text-breaker-red-deep">{error}</p> : null}
        </>
      )}
    </div>
  );
}
